import React, { useEffect, useState } from "react";
import { Repeat, ShieldAlert } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "../../lib/api";
import { fmtDateTime } from "../../lib/time";
import FeedbackCard from "../FeedbackCard";
import { Label } from "../common";

function EmployeeFeedback({ employeeId, onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/responses/feedback?employee_id=${employeeId}&limit=100`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [employeeId]);
  if (!rows) return <p className="py-10 text-center text-sm text-slate-400">Loading feedback…</p>;
  if (!rows.length) return <p className="py-10 text-center text-sm text-slate-400">No feedback yet.</p>;
  return (
    <div className="space-y-3" data-testid="emp-feedback-list">
      <p className="text-xs text-slate-400">{rows.length} most recent ratings · click any rating for full details</p>
      {rows.map((f) => <FeedbackCard key={f.id} f={f} onOpen={onOpen} />)}
    </div>
  );
}

function RepeatScansCard({ rs }) {
  if (!rs) return null;
  const flagged = rs.max_by_one >= 3;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="repeat-scans-card">
      <div className="flex items-center gap-2">
        <Repeat className="h-5 w-5 text-indigo-600" />
        <Label>Repeat Scans</Label>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Each QR/NFC open is logged against a pseudonymous browser/app id (first-party storage). It may reset if a visitor clears their browser, uses private browsing, or switches browsers.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Total scans", rs.total, "scan-total"],
          ["Unique browsers", rs.unique_visitors, "scan-unique"],
          ["Repeat visitors", rs.repeat_visitors, "scan-repeat"],
          ["Most by one browser", rs.max_by_one, "scan-max"],
        ].map(([lbl, val, tid]) => (
          <div key={tid} className="rounded-lg bg-slate-50 p-3 text-center" data-testid={tid}>
            <div className="font-head text-2xl font-bold text-slate-900">{val}</div>
            <div className="mt-0.5 text-xs font-semibold text-slate-500">{lbl}</div>
          </div>
        ))}
      </div>
      {rs.total > 0 && rs.daily?.length > 0 && (
        <div className="mt-5" data-testid="repeat-scans-timeline">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scans per day — last 30 days</span>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-indigo-200" /> First-time</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-indigo-600" /> Repeat</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 160 }}>
            <ResponsiveContainer>
              <BarChart data={rs.daily} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap={1}>
                <CartesianGrid vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94A3B8" }} interval={4} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} width={34} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  labelFormatter={(l) => l} formatter={(v, n) => [v, n === "repeat" ? "Repeat scans" : "First-time scans"]} />
                <Bar dataKey="first" stackId="s" fill="#C7D2FE" radius={[0, 0, 0, 0]} />
                <Bar dataKey="repeat" stackId="s" fill="#4338CA" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {flagged && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="repeat-scans-flag">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>One browser scanned this QR <strong>{rs.max_by_one}×</strong>. Repeated scans from a single browser are flagged in the Trust Center as a <strong>“repeat visitor”</strong> signal on any feedback submitted.</span>
        </div>
      )}
      {rs.top?.length > 0 ? (
        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2">Browser id</th><th className="px-3 py-2">Scans</th><th className="px-3 py-2">Channel</th><th className="px-3 py-2">Last seen</th></tr>
            </thead>
            <tbody>
              {rs.top.map((v) => (
                <tr key={`${v.visitor}-${v.last_seen}`} className="border-t border-slate-100" data-testid={`repeat-visitor-row-${v.visitor}`}>
                  <td className="px-3 py-2 font-mono text-slate-700">…{v.visitor}</td>
                  <td className="px-3 py-2 font-bold text-slate-900">{v.count}×</td>
                  <td className="px-3 py-2 text-slate-600">{(v.sources || []).map((s) => s.toUpperCase()).join(" · ") || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDateTime(v.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-500" data-testid="repeat-scans-empty">No repeat scans yet — every scan so far is from a distinct browser.</p>
      )}
    </div>
  );
}

export default function EmployeeHistoryTab({ cv, employeeId, onOpenFeedback }) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="feedback-history-card">
        <Label>Feedback history</Label>
        <div className="mt-4">
          <EmployeeFeedback employeeId={employeeId} onOpen={onOpenFeedback} />
        </div>
      </div>
      <RepeatScansCard rs={cv?.repeat_scans} />
    </div>
  );
}
